import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SkillGapAnalysis = ({ skillRoadmap = [], skillsMissing = [] }) => {
  const [expandedSkill, setExpandedSkill] = useState(null);
  const [activeCategory, setActiveCategory] = useState('critical');

  // Transform skillsMissing into the format expected by the UI
  const skillGaps = {
    critical: (skillsMissing || [])
      .filter((skill) => (skill.severity || 'recommended').toLowerCase() === 'critical')
      .map((skill, index) => ({
        id: `critical-${index}`,
        name: skill.name || `Skill ${index + 1}`,
        category: skill.category || 'Skill Gap',
        importance: skill.reason || `Critical requirement for this role`,
        description: skill.reason || `This skill is essential for the target role.`,
        marketDemand: typeof skill.market_demand === 'number' ? skill.market_demand : 85,
        learningTime: skill.estimated_time || "4-6 weeks",
        difficulty: "Intermediate",
        resources: []
      })),
    recommended: (skillsMissing || [])
      .filter((skill) => (skill.severity || 'recommended').toLowerCase() === 'recommended')
      .map((skill, index) => ({
        id: `recommended-${index}`,
        name: skill.name || `Skill ${index + 1}`,
        category: skill.category || 'Skill Gap',
        importance: skill.reason || `Recommended for better job fit`,
        description: skill.reason || `This skill will strengthen your profile.`,
        marketDemand: typeof skill.market_demand === 'number' ? skill.market_demand : 75,
        learningTime: skill.estimated_time || "4-6 weeks",
        difficulty: "Intermediate",
        resources: []
      })),
    bonus: (skillsMissing || [])
      .filter((skill) => (skill.severity || 'recommended').toLowerCase() === 'bonus')
      .map((skill, index) => ({
        id: `bonus-${index}`,
        name: skill.name || `Skill ${index + 1}`,
        category: skill.category || 'Skill Gap',
        importance: skill.reason || `Bonus skill for competitive advantage`,
        description: skill.reason || `This skill can give you an edge.`,
        marketDemand: typeof skill.market_demand === 'number' ? skill.market_demand : 65,
        learningTime: skill.estimated_time || "4-6 weeks",
        difficulty: "Intermediate",
        resources: []
      }))
  };

  // If no skills are found, show a message
  const hasSkills = skillGaps.critical.length > 0 || skillGaps.recommended.length > 0 || skillGaps.bonus.length > 0;

  const categories = [
    { id: 'critical', label: 'Critical', icon: 'AlertTriangle', color: 'text-error', count: skillGaps?.critical?.length },
    { id: 'recommended', label: 'Recommended', icon: 'Info', color: 'text-warning', count: skillGaps?.recommended?.length },
    { id: 'bonus', label: 'Bonus', icon: 'Plus', color: 'text-accent', count: skillGaps?.bonus?.length }
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-success/10 text-success';
      case 'Intermediate': return 'bg-warning/10 text-warning';
      case 'Advanced': return 'bg-error/10 text-error';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'course': return 'PlayCircle';
      case 'book': return 'Book';
      case 'project': return 'Code';
      case 'certification': return 'Award';
      case 'documentation': return 'FileText';
      case 'tutorial': return 'Video';
      case 'tool': return 'Settings';
      default: return 'Link';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-6">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Skill Gap Analysis</h3>
            <p className="text-sm text-muted-foreground">Detailed breakdown of skills to develop for your target role</p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setActiveCategory(category?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  activeCategory === category?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={category?.icon} size={16} />
                <span>{category?.label}</span>
                <span className="bg-background/20 text-xs px-2 py-0.5 rounded-full">
                  {category?.count}
                </span>
              </button>
            ))}
          </div>

          {/* Skills List */}
          <div className="space-y-4">
            {skillGaps?.[activeCategory]?.map((skill) => (
              <div key={skill?.id} className="border border-border rounded-lg overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-lg font-semibold text-foreground">{skill?.name}</h4>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                          {skill?.category}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDifficultyColor(skill?.difficulty)}`}>
                          {skill?.difficulty}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground">{skill?.importance}</p>
                      
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <Icon name="TrendingUp" size={14} className="text-primary" />
                          <span className="text-foreground">{skill?.marketDemand}% demand</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Clock" size={14} className="text-muted-foreground" />
                          <span className="text-muted-foreground">{skill?.learningTime}</span>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setExpandedSkill(expandedSkill === skill?.id ? null : skill?.id)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
                    >
                      <Icon 
                        name={expandedSkill === skill?.id ? "ChevronUp" : "ChevronDown"} 
                        size={20} 
                        className="text-muted-foreground" 
                      />
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedSkill === skill?.id && (
                  <div className="border-t border-border bg-muted/30 p-4 space-y-4">
                    <div>
                      <h5 className="font-medium text-foreground mb-2">Why This Skill Matters</h5>
                      <p className="text-sm text-muted-foreground leading-relaxed">{skill?.description}</p>
                    </div>

                    <div>
                      <h5 className="font-medium text-foreground mb-3">Learning Resources</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {skill?.resources?.map((resource, index) => (
                          <div key={index} className="bg-card border border-border rounded-lg p-3">
                            <div className="flex items-start space-x-3">
                              <Icon name={getResourceIcon(resource?.type)} size={16} className="text-primary mt-0.5" />
                              <div className="flex-1 space-y-1">
                                <div className="font-medium text-foreground text-sm">{resource?.title}</div>
                                {resource?.provider && (
                                  <div className="text-xs text-muted-foreground">{resource?.provider}</div>
                                )}
                                <div className="flex items-center space-x-2 text-xs">
                                  {resource?.duration && (
                                    <span className="bg-muted text-muted-foreground px-2 py-0.5 rounded">
                                      {resource?.duration}
                                    </span>
                                  )}
                                  {resource?.difficulty && (
                                    <span className={`px-2 py-0.5 rounded ${getDifficultyColor(resource?.difficulty)}`}>
                                      {resource?.difficulty}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <Button variant="outline" size="sm" iconName="BookOpen" iconPosition="left">
                        Start Learning
                      </Button>
                      <Button variant="ghost" size="sm" iconName="Bookmark" iconPosition="left">
                        Save for Later
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Summary Stats */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-error">{skillGaps?.critical?.length}</div>
                <div className="text-sm text-muted-foreground">Critical Skills</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-warning">{skillGaps?.recommended?.length}</div>
                <div className="text-sm text-muted-foreground">Recommended</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-accent">{skillGaps?.bonus?.length}</div>
                <div className="text-sm text-muted-foreground">Bonus Skills</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillGapAnalysis;